// API 客户端 - 与 FastAPI 后端通信
// 部署时使用相对路径，开发时使用完整 URL
const isDev = import.meta.env.DEV;
const API_BASE_URL = isDev ? (import.meta.env.VITE_API_URL || 'http://localhost:8000') : '';

export interface SearchRequest {
  benchmark: string;
  limit: number;
  sort_by: string;
}

export interface Paper {
  paper_id: string;
  title: string;
  abstract: string;
  year: number;
  citation_count: number;
  authors: string;
  url: string;
  pdf_url?: string;
  has_pdf: boolean;
}

export interface SearchResponse {
  papers: Paper[];
}

export interface AnalyzeOptions {
  max_workers: number;
  download_pdf: boolean;
}

export interface StartAnalyzeRequest {
  papers: Paper[];
  benchmark: string;
  options: AnalyzeOptions;
}

export interface StartAnalyzeResponse {
  task_id: string;
  total: number;
}

export interface AnalysisResult {
  method: string;
  innovation: string;
  accuracy: string;
  metrics: string;
}

export interface AnalyzedPaper extends Paper {
  analysis_status: 'pending' | 'analyzing_simple' | 'simple_completed' | 'analyzing_fulltext' | 'completed' | 'simple_only' | 'failed';
  analysis?: AnalysisResult;
  analysis_source?: 'simple' | 'fulltext';
}

export interface TaskStatus {
  task_id: string;
  status: 'running' | 'completed' | 'failed';
  progress: number;
  completed: number;
  total: number;
  papers: AnalyzedPaper[];
}

// 搜索论文
export async function searchPapers(request: SearchRequest): Promise<SearchResponse> {
  const response = await fetch(`${API_BASE_URL}/api/search`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(request),
  });

  if (!response.ok) {
    const error = await response.text();
    throw new Error(`搜索失败: ${error}`);
  }

  return response.json();
}

// 启动分析任务
export async function startAnalysis(request: StartAnalyzeRequest): Promise<StartAnalyzeResponse> {
  const response = await fetch(`${API_BASE_URL}/api/analyze/start`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(request),
  });

  if (!response.ok) {
    const error = await response.text();
    throw new Error(`启动分析失败: ${error}`);
  }

  return response.json();
}

// 获取任务状态
export async function getTaskStatus(taskId: string): Promise<TaskStatus> {
  const response = await fetch(`${API_BASE_URL}/api/analyze/status/${taskId}`);

  if (!response.ok) {
    const error = await response.text();
    throw new Error(`获取状态失败: ${error}`);
  }

  return response.json();
}

// 获取分析结果
export async function getAnalysisResult(taskId: string) {
  const response = await fetch(`${API_BASE_URL}/api/analyze/result/${taskId}`);

  if (!response.ok) {
    const error = await response.text();
    throw new Error(`获取结果失败: ${error}`);
  }

  return response.json();
}

// 轮询任务状态
export async function pollTaskStatus(
  taskId: string,
  onProgress: (status: TaskStatus) => void,
  interval: number = 1000,
  maxRetries: number = 3
): Promise<TaskStatus> {
  return new Promise((resolve, reject) => {
    let retries = 0;

    const check = async () => {
      try {
        const status = await getTaskStatus(taskId);
        retries = 0; // 成功时重置重试计数
        onProgress(status);

        if (status.status === 'completed' || status.status === 'failed') {
          resolve(status);
        } else {
          setTimeout(check, interval);
        }
      } catch (error) {
        retries++;
        console.warn(`[pollTaskStatus] 获取状态失败 (${retries}/${maxRetries}):`, error);

        // 如果任务不存在（404），直接失败
        if (error instanceof Error && error.message.includes('404')) {
          reject(error);
          return;
        }

        // 未达到最大重试次数，继续轮询
        if (retries < maxRetries) {
          setTimeout(check, interval);
        } else {
          // 超过最大重试次数，返回错误
          reject(new Error(`轮询失败，已重试 ${maxRetries} 次: ${error}`));
        }
      }
    };

    check();
  });
}
